/**
 * Monaco Editor providers for MethodScript language support.
 * Extracted from editor.ts for better code organization and type safety.
 */

import { findSIPrefixedValues, createSIPrefixHoverContent } from 'methodscript-core';
import * as monaco from 'monaco-editor';
import { closesBlock, opensBlock } from 'shared-monaco-methodscript';

import { webEditorLanguageService } from './language-service-bridge.js';

/**
 * Create a hover provider for MethodScript
 * Provides documentation for SI prefix literals and command documentation
 */
export function createMethodScriptHoverProvider(monacoInstance: typeof monaco): monaco.languages.HoverProvider {
    return {
        provideHover: async (model: monaco.editor.ITextModel, position: monaco.Position): Promise<monaco.languages.Hover | null> => {
            const line = model.getLineContent(position.lineNumber);
            const column = position.column - 1;

            // First, check for SI prefix literals (e.g., 100G, 15m, 42k)
            // MethodScript: Floating-point values are signed integers followed by SI prefix (no decimal point)
            const siMatches = findSIPrefixedValues(line);
            for (const { match, index: matchIndex } of siMatches) {
                const matchEnd = matchIndex + match.length;

                // Check if cursor is within this match
                if (column >= matchIndex && column <= matchEnd) {
                    const content = createSIPrefixHoverContent(match);
                    if (content) {
                        return {
                            range: new monacoInstance.Range(position.lineNumber, matchIndex + 1, position.lineNumber, matchEnd + 1),
                            contents: [{ value: content.title }, { value: content.detail }],
                        };
                    }
                }
            }

            // If not an SI prefix, check for command documentation using context-aware hover
            const word = model.getWordAtPosition(position);
            if (!word) return null;

            // Use context-aware hover to detect if we're in a VarType parameter position
            const lineText = line;
            const wordStart = word.startColumn - 1; // Convert to 0-based
            const hoverDoc = await webEditorLanguageService.getHover(word.word, lineText, wordStart);

            if (hoverDoc) {
                return {
                    range: new monacoInstance.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
                    contents: [{ value: hoverDoc.title }, { value: hoverDoc.documentation }],
                };
            }

            return null;
        },
    };
}

/**
 * Create a signature help provider for MethodScript
 * Provides parameter hints for all commands with parameters
 */
export function createMethodScriptSignatureHelpProvider(): monaco.languages.SignatureHelpProvider {
    return {
        signatureHelpTriggerCharacters: [' '],
        provideSignatureHelp: async (model: monaco.editor.ITextModel, position: monaco.Position): Promise<monaco.languages.SignatureHelpResult | null> => {
            const lineContent = model.getLineContent(position.lineNumber);
            const cursorColumn = position.column - 1; // Convert to 0-based

            const result = await webEditorLanguageService.getSignatureHelp(lineContent, cursorColumn);

            if (!result) {
                return null;
            }

            return {
                value: {
                    signatures: result.signatures.map((sig) => ({
                        label: sig.label,
                        documentation: sig.documentation,
                        parameters: sig.parameters.map((param) => ({
                            label: param.label,
                            documentation: param.documentation,
                        })),
                    })),
                    activeSignature: result.activeSignature,
                    activeParameter: result.activeParameter,
                },
                dispose: () => {
                    // No cleanup needed
                },
            };
        },
    };
}

/**
 * Create a folding range provider for MethodScript
 * Provides code folding for blocks (if/endif, loop/endloop) and comment blocks
 */
export function createMethodScriptFoldingRangeProvider(monacoInstance: typeof monaco): monaco.languages.FoldingRangeProvider {
    return {
        provideFoldingRanges: (model: monaco.editor.ITextModel): monaco.languages.ProviderResult<monaco.languages.FoldingRange[]> => {
            const text = model.getValue();
            const lines = text.split('\n');
            const ranges: monaco.languages.FoldingRange[] = [];
            const stack: Array<{ start: number; type: string }> = [];

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                // Start of block
                if (opensBlock(line)) {
                    stack.push({ start: i, type: line.split(/\s+/)[0] });
                }

                // End of block
                if (closesBlock(line)) {
                    const block = stack.pop();
                    if (block) {
                        ranges.push({
                            start: block.start + 1,
                            end: i + 1,
                            kind: monacoInstance.languages.FoldingRangeKind.Region,
                        });
                    }
                }

                // Comment blocks
                if (line.startsWith('#')) {
                    let commentEnd = i;
                    while (commentEnd + 1 < lines.length && lines[commentEnd + 1].trim().startsWith('#')) {
                        commentEnd++;
                    }
                    if (commentEnd > i) {
                        ranges.push({
                            start: i + 1,
                            end: commentEnd + 1,
                            kind: monacoInstance.languages.FoldingRangeKind.Comment,
                        });
                        i = commentEnd;
                    }
                }
            }

            return ranges;
        },
    };
}
